import React from 'react';
import PropTypes from 'prop-types';
import { Box, Button, Grid, Typography, Link as MuiLink } from '@material-ui/core';
import { FormattedMessage, useIntl } from 'react-intl';
import { Input, Checkbox, FileInput } from 'components/FormInputs';
import AcceptTerms from 'components/shared/AcceptTerms';
import { validateNewUploadIDPatch } from 'logic/userForm';
import { useFormik } from 'components/shared/Formik';

const uploadDocument = <FormattedMessage id="Upload document" defaultMessage="Upload document" />;

const uploadFrontSide = (
  <FormattedMessage id="Upload front side" defaultMessage="Upload front side" />
);

const uploadBackSide = <FormattedMessage id="Upload back side" defaultMessage="Upload back side" />;

const Instructions = ({ reset }) => (
  <>
    <Grid item xs={12}>
      <Typography paragraph color="textSecondary">
        <FormattedMessage
          id="userform.makeSurePhotoClear"
          defaultMessage="Make sure you photo is clear to read, with no blur or glare"
        />
      </Typography>
      <Typography paragraph color="textSecondary">
        <FormattedMessage
          id="userform.ifImageNotClear"
          defaultMessage="If the image is not clear to read, blur or glare, {clickHere} to try again"
          values={{
            clickHere: (
              <MuiLink onClick={reset}>
                <FormattedMessage id="click here" defaultMessage="click here" />
              </MuiLink>
            ),
          }}
        />
      </Typography>
    </Grid>
  </>
);

Instructions.propTypes = {
  reset: PropTypes.func,
};

const Form = ({ onSubmit, sidesCount, user, uploading }) => {
  const intl = useIntl();
  const [previews, setPreviews] = React.useState({
    front: null,
    back: null,
  });
  const frontRef = React.useRef(null);
  const backRef = React.useRef(null);

  const formik = useFormik({
    initialValues: {
      first_name: user.first_name,
      id_last_name: user.last_name,
      id_first_name: '',
      termsOfPayment: !!user.mango_user_id,
      front: null,
      back: null,
      sidesCount,
    },
    onSubmit: async (values) => {
      return onSubmit(values);
    },
    enableReinitialize: true,
    validate: validateNewUploadIDPatch({ intl }),
    validateOnChange: false,
  });

  const {
    handleSubmit,
    handleChange,
    handleBlur,
    errors,
    values,
    isSubmitting,
    setFieldValue,
    resetForm,
  } = formik;

  const setPreview = (name, value) => {
    const reader = new FileReader();

    reader.onload = (event) => {
      setPreviews({
        ...previews,
        [name]: event.target.result,
      });
    };

    reader.readAsDataURL(value);
  };

  const reset = () => {
    setPreviews({ front: null, back: null });
    resetForm();

    if (frontRef.current) {
      frontRef.current.value = null;
    }

    if (backRef.current) {
      backRef.current.value = null;
    }
  };

  React.useEffect(() => {
    if (sidesCount !== values.sidesCount) {
      reset();
    }
  }, [sidesCount]);

  const noFiles = !values.front || (sidesCount === 2 && !values.back);

  return (
    <Box mt={4}>
      <form onSubmit={handleSubmit} noValidate>
        <Grid container spacing={2} justifyContent="center">
          <Grid item xs={12} sm={6}>
            <Box textAlign="center">
              <Button
                color="primary"
                component="label"
                htmlFor="front_side_file"
                variant={previews.front ? 'outlined' : 'contained'}
                onClick={() => frontRef.current.click()}
                size="small"
              >
                {!previews.front && (
                  <Typography htmlFor="front_side_file">
                    {sidesCount === 1 ? uploadDocument : uploadFrontSide}
                  </Typography>
                )}
                {previews.front && <img src={previews.front} alt="" />}
                <FileInput
                  name="front"
                  onChange={(e) => {
                    if (!e.target.files) {
                      return;
                    }

                    setFieldValue('front', e.target.files[0]);
                    setPreview('front', e.target.files[0]);
                  }}
                  fileRef={(n) => {
                    frontRef.current = n;
                  }}
                />
              </Button>
            </Box>
          </Grid>
          {sidesCount > 1 && (
            <Grid item xs={12} sm={6}>
              <Box textAlign="center">
                <Button
                  color="primary"
                  component="label"
                  htmlFor="back_side_file"
                  variant={previews.back ? 'outlined' : 'contained'}
                  onClick={() => backRef.current.click()}
                  size="small"
                >
                  {!previews.back && (
                    <Typography htmlFor="back_side_file">{uploadBackSide}</Typography>
                  )}
                  {previews.back && <img src={previews.back} alt="" />}
                  <FileInput
                    value={values.back}
                    name="back"
                    onChange={(e) => {
                      if (!e.target.files) {
                        return;
                      }

                      setFieldValue('back', e.target.files[0]);
                      setPreview('back', e.target.files[0]);
                    }}
                    fileRef={(n) => {
                      backRef.current = n;
                    }}
                  />
                </Button>
              </Box>
            </Grid>
          )}

          {!noFiles && <Instructions reset={reset} />}
          <Grid item xs={12}>
            <Box textAlign="left">
              <Checkbox
                name="termsOfPayment"
                value={values.termsOfPayment}
                onChange={handleChange}
                label={<AcceptTerms />}
              />
            </Box>
          </Grid>
          {((sidesCount === 2 && previews.back && previews.front) ||
            (sidesCount === 1 && previews.front)) && (
            <>
              <Grid item xs={12}>
                <hr />
                <Typography variant="body1">
                  <FormattedMessage
                    id="userForm.writeAllNames"
                    defaultMessage="Write all your names as they are written on your document"
                  />
                </Typography>
              </Grid>

              <Grid item xs={12} md={6}>
                <Input
                  name="id_first_name"
                  errors={errors}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  value={values.id_first_name}
                  label={<FormattedMessage id="userform.firstName" defaultMessage="First name" />}
                  required
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Input
                  name="id_last_name"
                  errors={errors}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  value={values.id_last_name}
                  label={<FormattedMessage id="userform.lastName" defaultMessage="Last name" />}
                  required
                />
              </Grid>

              <Grid item xs={12}>
                <Typography variant="body1">
                  <FormattedMessage
                    id="userForm.writeChooseNames"
                    defaultMessage="Choose what name you want to have on your public account"
                  />
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Input
                  name="first_name"
                  errors={errors}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  value={values.first_name}
                  label={<FormattedMessage id="userform.firstName" defaultMessage="First name" />}
                  required
                  disabled={!user.can_change_name}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Input
                  name="id_last_name"
                  errors={errors}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  value={values.id_last_name}
                  label={<FormattedMessage id="userform.lastName" defaultMessage="Last name" />}
                  required
                  disabled
                />
              </Grid>
            </>
          )}

          <Grid item xs={12}>
            <Box textAlign="left" mb={7}>
              <Button
                variant="contained"
                color="primary"
                type="submit"
                disabled={!values.termsOfPayment || isSubmitting || noFiles || uploading}
              >
                {uploading && (
                  <FormattedMessage
                    id="userform.uploading"
                    defaultMessage="Uploading... please wait"
                  />
                )}
                {!uploading && <FormattedMessage id="Save" defaultMessage="Save" />}
              </Button>
            </Box>
          </Grid>
        </Grid>
      </form>
    </Box>
  );
};

Form.propTypes = {
  onSubmit: PropTypes.func.isRequired,
  sidesCount: PropTypes.number,
  user: PropTypes.shape({
    first_name: PropTypes.string,
    last_name: PropTypes.string,
    mango_user_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    can_change_name: PropTypes.bool,
  }),
  uploading: PropTypes.bool,
};

export default Form;
